import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Target, Trophy, Users } from 'lucide-react';
import SectorHeatmap from './SectorHeatmap';
import { API } from '../config/api';

/**
 * Combined "Heatmap" view that includes:
 *   1. Predicción de Clasificación — what would the field look like if every
 *      driver delivered their theoretical best lap?
 *   2. Sector × Lap heatmap (F1 purple / green / yellow / red).
 *
 * Lives between "Parciales" and "Vuelta x Vuelta" in the RaceComparison
 * navigation. Drivers obsess over the gap between their actual best and
 * their theoretical best, and what position they would have if they could
 * stitch together a perfect lap.
 */

function fmtLap(s) {
  if (!s || s <= 0) return '—';
  const m = Math.floor(s / 60);
  const sec = (s % 60).toFixed(3);
  return m > 0 ? `${m}:${sec.padStart(6, '0')}` : sec;
}

export default function SectorHeatmapView({ race, selectedDrivers = [] }) {
  const [onlySelected, setOnlySelected] = useState(false);

  // Build prediction table: per driver, the actual best vs. theoretical best
  // (sum of own personal best per sector). Then re-rank by potential to show
  // where each driver WOULD be if they could put their perfect lap together.
  //
  // IMPORTANT: positions (real & potential) are ALWAYS computed against the
  // full field — otherwise the predicted position would be meaningless. The
  // "Solo seleccionados" toggle only filters which rows are rendered.
  const prediction = useMemo(() => {
    const drivers = race?.drivers || [];
    const rows = drivers
      .map((d) => {
        const personalBestPerSector = {};
        for (const s of (d.sector_times || [])) {
          const valid = (s.times || []).filter((t) => t > 0);
          if (valid.length) personalBestPerSector[s.sector_number] = Math.min(...valid);
        }
        const sectors = Object.values(personalBestPerSector);
        const potential = sectors.length ? sectors.reduce((a, b) => a + b, 0) : 0;
        const actual = d.best_lap || 0;
        const margin = (actual > 0 && potential > 0) ? actual - potential : null;
        return {
          driver_number: d.driver_number,
          driver_name: d.driver_name,
          actual,
          potential,
          margin,
        };
      })
      .filter((r) => r.actual > 0 || r.potential > 0);

    // Rank #1: by actual best (current standings).
    const actualSorted = [...rows]
      .filter((r) => r.actual > 0)
      .sort((a, b) => a.actual - b.actual);
    const actualPosition = {};
    actualSorted.forEach((r, i) => { actualPosition[r.driver_number] = i + 1; });

    // Rank #2: by potential best (predicted standings).
    const potentialSorted = [...rows]
      .filter((r) => r.potential > 0)
      .sort((a, b) => a.potential - b.potential);
    const potentialPosition = {};
    potentialSorted.forEach((r, i) => { potentialPosition[r.driver_number] = i + 1; });

    return rows
      .map((r) => ({
        ...r,
        actualPos: actualPosition[r.driver_number] || null,
        potentialPos: potentialPosition[r.driver_number] || null,
        positionDelta: (actualPosition[r.driver_number] && potentialPosition[r.driver_number])
          ? actualPosition[r.driver_number] - potentialPosition[r.driver_number]
          : null,
      }))
      .sort((a, b) => (a.potential || Infinity) - (b.potential || Infinity));
  }, [race]);

  if (!race) return null;

  // Filter rows for display. The positions inside each row were calculated
  // against the full field above, so they stay meaningful when filtered.
  const visibleRows = (onlySelected && selectedDrivers.length > 0)
    ? prediction.filter((r) => selectedDrivers.includes(r.driver_number))
    : prediction;
  const canFilter = selectedDrivers.length > 0;

  return (
    <div className="space-y-6">
      {/* Prediction card */}
      <Card className="bg-gradient-to-r from-purple-950/40 to-neutral-900 border-purple-700/40" data-testid="prediction-card">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="text-xl text-white flex items-center gap-2">
                <Target className="w-5 h-5 text-purple-400" />
                Predicción de Clasificación
              </CardTitle>
              <CardDescription className="text-gray-400">
                Si cada piloto pudiera juntar sus mejores parciales en una sola vuelta. Útil para visualizar qué posición REAL le corresponde a cada uno cuando "todo sale bien".
              </CardDescription>
            </div>
            {/* Filter toggle: focus on the head-to-head between the pilots
                the user has selected on the left panel. */}
            <div className="flex items-center bg-neutral-800 rounded-lg p-0.5 border border-neutral-700" data-testid="prediction-filter-toggle">
              <button
                type="button"
                onClick={() => setOnlySelected(false)}
                data-testid="prediction-filter-all"
                className={`px-3 h-8 text-xs rounded-md transition flex items-center gap-1.5 ${
                  !onlySelected
                    ? 'bg-purple-600 text-white shadow-sm'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                <Users className="w-3.5 h-3.5" />
                Todos
              </button>
              <button
                type="button"
                onClick={() => canFilter && setOnlySelected(true)}
                data-testid="prediction-filter-selected"
                disabled={!canFilter}
                title={canFilter ? '' : 'Seleccioná al menos un piloto en el panel izquierdo'}
                className={`px-3 h-8 text-xs rounded-md transition flex items-center gap-1.5 ${
                  onlySelected
                    ? 'bg-purple-600 text-white shadow-sm'
                    : canFilter
                    ? 'text-gray-400 hover:text-white'
                    : 'text-gray-600 cursor-not-allowed'
                }`}
              >
                <Target className="w-3.5 h-3.5" />
                Solo seleccionados
                {canFilter && (
                  <span className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold bg-purple-900/60 text-purple-200">
                    {selectedDrivers.length}
                  </span>
                )}
              </button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-800/50 border-b border-neutral-700">
                <tr>
                  <th className="text-center py-2 px-2 text-gray-400 font-medium w-12">Pot.</th>
                  <th className="text-left py-2 px-2 text-gray-400 font-medium">Piloto</th>
                  <th className="text-center py-2 px-2 text-gray-400 font-medium">Pos. real</th>
                  <th className="text-right py-2 px-2 text-gray-400 font-medium">Vuelta actual</th>
                  <th className="text-right py-2 px-2 text-purple-300 font-medium">Vuelta potencial</th>
                  <th className="text-right py-2 px-3 text-gray-400 font-medium">Margen</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.slice(0, 20).map((r, idx) => {
                  const gain = r.positionDelta != null && r.positionDelta > 0;
                  const loss = r.positionDelta != null && r.positionDelta < 0;
                  return (
                    <tr
                      key={r.driver_number}
                      className="border-b border-neutral-900 hover:bg-neutral-800/40"
                      data-testid={`prediction-row-${r.driver_number}`}
                    >
                      <td className="text-center py-2 px-2">
                        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold ${
                          idx === 0 ? 'bg-purple-600 text-white' :
                          idx === 1 ? 'bg-purple-700/50 text-purple-200' :
                          idx === 2 ? 'bg-purple-800/50 text-purple-300' :
                          'bg-neutral-800 text-gray-400'
                        }`}>
                          {r.potentialPos || '—'}
                        </span>
                      </td>
                      <td className="text-left py-2 px-2 text-white">
                        <div className="font-semibold truncate" title={r.driver_name}>
                          #{r.driver_number} {r.driver_name}
                        </div>
                      </td>
                      <td className="text-center py-2 px-2 text-gray-300 font-mono">
                        {r.actualPos || '—'}
                        {gain && (
                          <span className="ml-1 text-[10px] text-green-400" data-testid={`prediction-gain-${r.driver_number}`}>
                            ▲{r.positionDelta}
                          </span>
                        )}
                        {loss && (
                          <span className="ml-1 text-[10px] text-red-400" data-testid={`prediction-loss-${r.driver_number}`}>
                            ▼{Math.abs(r.positionDelta)}
                          </span>
                        )}
                      </td>
                      <td className="text-right py-2 px-2 text-gray-200 font-mono">{fmtLap(r.actual)}</td>
                      <td className="text-right py-2 px-2 text-purple-300 font-mono font-bold">{fmtLap(r.potential)}</td>
                      <td className="text-right py-2 px-3 text-yellow-400 font-mono">
                        {r.margin != null && r.margin > 0.001 ? `+${r.margin.toFixed(3)}s` : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {prediction.length > 20 && (
            <div className="text-center py-2 border-t border-neutral-800 text-xs text-gray-500">
              {onlySelected && canFilter
                ? `Mostrando ${visibleRows.length} de ${selectedDrivers.length} seleccionados (rankeo contra los ${prediction.length} pilotos)`
                : `Mostrando los 20 con mejor potencial · ${prediction.length} pilotos totales`}
            </div>
          )}
          {visibleRows.length === 0 && onlySelected && (
            <div className="text-center py-6 border-t border-neutral-800 text-xs text-gray-500" data-testid="prediction-empty">
              No hay pilotos seleccionados con datos válidos.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sector × Lap heatmap */}
      <SectorHeatmap race={race} />
    </div>
  );
}
